// chartUtils.ts

import { TooltipItem } from "chart.js";
// 📦 קומפוננטת עטיפה ל־Chart
import React, { ReactNode } from "react";
import { Box } from "@mui/material";
// 🟦 תוסף שמצייר את ערכי הקצה של קווים
export const finalValueLabelPlugin = {
  id: "final-value-label-plugin",
  afterDatasetsDraw(chart: any) {
    const { ctx, data, chartArea } = chart;

    data.datasets.forEach((dataset: any, datasetIndex: number) => {
      if (dataset.type !== "line") return;
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta?.data?.length) return;

      // ✅ בדיקה אמינה אם ה־dataset מוסתר
      const isVisible = chart.isDatasetVisible(datasetIndex);
      if (!isVisible) return;

      const lastPoint = meta.data[meta.data.length - 1];
      const { x, y } = lastPoint.tooltipPosition();
      const label = dataset.finalValueLabel || dataset.finalValue || "";

      if (!label) return;

      ctx.save();
      ctx.fillStyle = dataset.borderColor || "#000";
      ctx.fillRect(chartArea.right + 8, y - 10, 60, 20);
      ctx.fillStyle = "#fff";
      ctx.font = "12px Arial";
      ctx.textBaseline = "middle";

      ctx.fillText(label, chartArea.right + 14, y);
      ctx.restore();
    });
  }
};

// 🗓️ פורמט תקופה YYYYMM ל-MM-YYYY
export const formatPeriodLabel = (period: number | string): string => {
  const str = String(period);
  return `${str.slice(4, 6)}-${str.slice(0, 4)}`;
};

// 🎨 גוונים לפי מזהה וסולם Alpha
export const colorByAlpha = (
  baseColor: string,
  identifier: number,
  minAlpha = 0.33,
  maxAlpha = 0.99
): string => {
  const r = parseInt(baseColor.slice(1, 3), 16);
  const g = parseInt(baseColor.slice(3, 5), 16);
  const b = parseInt(baseColor.slice(5, 7), 16);
  const alpha = minAlpha + (identifier % 10) * ((maxAlpha - minAlpha) / 10);
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
};



interface ChartContainerProps {
  height?: number;
  width?: string | number;
  children: React.ReactNode;
}

export const ChartContainer: React.FC<ChartContainerProps> = ({
  height = 300,
  width = '100%',
  children
}) => (
  <Box sx={{ position: 'relative', width, height }}>
    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,direction: 'ltr' }}>
      {children}
    </Box>
  </Box>
);

// 🛠️ Tooltip footer סה"כ לכל stack
export const stackTotalTooltipFooter = (tooltipItems: TooltipItem<"bar">[]) => {
  if (!tooltipItems.length) return "";
  const dataIndex = tooltipItems[0].dataIndex;
  const chart = tooltipItems[0].chart;
  const datasets = chart.data.datasets;

  const total = datasets.reduce((sum, dataset) => {
    const value = (dataset.data?.[dataIndex] as number) || 0;
    return sum + value;
  }, 0);

  return `סה\"כ: ${total.toLocaleString()} ₪`;
};
export const buildChartOptions = (config: {
  isLineChart: boolean;
  isStacked: boolean;
  showFooterTotal?: boolean;
  customTooltip?: any;
  onDatasetClick?: (dataset: any, dataIndex: number) => void;
  formatValue?: (value: number) => string;
  tooltip?: any;
  beginAtZero?: boolean;
}) => {
  const format = config.formatValue || ((v: number) => v.toString());

  const baseTooltipCallbacks = {
    label: (context: any) => `${context.dataset.label}: ${format(context.raw)}`,
    footer: config.showFooterTotal
      ? (tooltipItems: TooltipItem<"bar">[]) => {
          if (!tooltipItems.length) return '';
          const dataIndex = tooltipItems[0].dataIndex;
          const chart = tooltipItems[0].chart;
          const datasets = chart.data.datasets;
          const total = datasets.reduce((sum, dataset) => {
            const value = dataset.data?.[dataIndex] as number || 0;
            return sum + value;
          }, 0);
          return `סה\"כ: ${format(total)}`;
        }
      : undefined,
  };

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest' as const,
      intersect: true,
    },
    layout: defaultChartLayout,
    plugins: {
      legend: defaultChartLegend,
      title: { display: false },
      tooltip: config.tooltip || {
        callbacks: config.customTooltip || baseTooltipCallbacks,
      },
      datalabels: {
        display: false,
      },
      finalValueLabel: config.isLineChart,
    },
    scales: {
      x: { stacked: config.isStacked },
      y: {
        stacked: config.isStacked,
        beginAtZero: config.beginAtZero !== undefined ? config.beginAtZero : true, // ברירת מחדל: true
        grid: getHighlightedZeroLineGridConfig(), // תמיד הדגש את קו Y=0
      },
    },
   onClick: (_event: any, elements: any[]) => {
  if (elements.length === 0 || !config.onDatasetClick) return;

  for (const el of elements) {
    const chart = el.element.$context?.chart;
    const datasetIndex = el.datasetIndex;
    const dataset = chart?.config?.data?.datasets?.[datasetIndex];

    if (dataset) {
      config.onDatasetClick(dataset, el.index);
      break; // אפשר להפסיק אחרי הראשון התקף
    }
  }
}
  };
};

export interface BaseDataset {
  label: string;
  data: number[];
  backgroundColor: string;
  borderColor: string;
  finalValueLabel?: string;
  [key: string]: any;
}

export const buildLineDataset = (base: BaseDataset): any => ({
  type: "line",
  borderWidth: 2,
  pointRadius: 3,
  pointHoverRadius: 5,
  fill: false,
  spanGaps: base.spanGaps ?? false,
  ...base,
});

export const buildBarDataset = (base: BaseDataset): any => ({
  type: "bar",
  borderWidth: 0,
  borderRadius: 4,
  ...base,
});
export const buildAreaDataset = (base: BaseDataset) => ({
  ...base,
  type: "line",
  pointRadius: 3,
  pointHoverRadius: 4,
  borderWidth: 2,
  fill: true,
  stack: "stack1",
});


export const defaultChartLayout = {
  padding: {
    left: 0,
    right: 80,
    top: 10,
    bottom: 10,
  },
};

export const defaultChartLegend = {
  position: "top" as const,
};

// Plugin לציור מעגל חיצוני עבור קרנות נבחרות והודגשו
// תומך גם ב-Line/Bar charts (dataset אחד לכל קרן) וגם ב-Scatter charts (נקודות בודדות)
// ה-selectedFundId מועבר דרך chart.config.options.selectedFundId (Line/Bar)
// או דרך dataPoint.isSelected / dataPoint.isHighlighted (Scatter)
// צבעים: כתום להדגשה (highlighted), ורוד לבחירה (selected)
// מצויר אחרי הנקודות כדי להופיע מעליהן
export const selectedFundOutlinePlugin = {
  id: 'selectedFundOutline',
  afterDatasetsDraw(chart: any) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
      const meta = chart.getDatasetMeta(datasetIndex);

      meta.data.forEach((point: any, index: number) => {
        const dataPoint = dataset.data[index];

        // דלג על נקודות ריקות
        if (!dataPoint || dataPoint === null || dataPoint === undefined) return;

        let highlightType: 'selected' | 'highlighted' | null = null;

        // בדוק אם צריך להדגיש - Line/Bar charts (לפי selectedFundId ב-options)
        const selectedFundId = chart.config?.options?.selectedFundId;
        if (selectedFundId && dataset.fundId === selectedFundId) {
          highlightType = 'selected';
        }

        // בדוק אם צריך להדגיש - Scatter charts
        // עדיפות: highlighted על selected (אם קרן גם בתיק וגם נבחרה, תראה כתום)
        if (dataPoint.isSelected) {
          highlightType = 'selected';
        }
        if (dataPoint.isHighlighted) {
          highlightType = 'highlighted';
        }

        if (!highlightType) return;

        const { x, y } = point.getCenterPoint();

        // בחר צבע לפי סוג ההדגשה
        const strokeStyle = highlightType === 'highlighted'
          ? 'rgba(255, 152, 0, 1)'  // כתום להדגשה (קרנות בתיק)
          : 'rgba(236, 72, 153, 1)'; // ורוד לבחירה (קרנות שנבחרו)

        // צייר מעגל חיצוני
        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, 10, 0, 2 * Math.PI);
        ctx.strokeStyle = strokeStyle;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      });
    });
  },
};

// Plugin לציור שטחים אנכיים ברקע התרשים לפי buckets של חשיפה למניות (ציר X)
// פעיל רק כאשר chart.options.showStockExposureBands === true
// דורש chart.options.stockExposureBandsConfig = { buckets, colors, labels? }
// (buckets: {label,min,max}[], colors: Record<label,string>, labels?: Record<label,string> - שם תצוגה ידידותי, ברירת מחדל ל-label)
// אם למספר buckets רצופים יש אותו שם תצוגה (labels), הם מטופלים כאזור מאוחד לצורך מיקום הטקסט
export const stockExposureBandsPlugin = {
  id: 'stockExposureBands',
  beforeDatasetsDraw(chart: any) {
    const config = chart.config?.options?.stockExposureBandsConfig;
    if (!chart.config?.options?.showStockExposureBands || !config) return;

    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    if (!chartArea || !xScale) return;

    const { buckets, colors, labels } = config;

    ctx.save();
    buckets.forEach((bucket: { label: string; min: number; max: number }) => {
      const xStart = xScale.getPixelForValue(Math.max(bucket.min, xScale.min));
      const xEnd = xScale.getPixelForValue(Math.min(bucket.max, xScale.max));
      if (xEnd <= chartArea.left || xStart >= chartArea.right) return;

      const left = Math.max(xStart, chartArea.left);
      const right = Math.min(xEnd, chartArea.right);

      ctx.fillStyle = colors[bucket.label] || 'rgba(0,0,0,0.05)';
      ctx.globalAlpha = 0.15;
      ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top);
    });
    ctx.restore();

    // איחוד buckets רצופים עם אותו שם תצוגה לאזור טקסט אחד (כדי שלא יופיע אותו שם כמה פעמים ברצף)
    const displayName = (label: string) => (labels && labels[label]) || label;
    const mergedRegions: { name: string; left: number; right: number; color: string }[] = [];
    buckets.forEach((bucket: { label: string; min: number; max: number }) => {
      const xStart = xScale.getPixelForValue(Math.max(bucket.min, xScale.min));
      const xEnd = xScale.getPixelForValue(Math.min(bucket.max, xScale.max));
      if (xEnd <= chartArea.left || xStart >= chartArea.right) return;

      const left = Math.max(xStart, chartArea.left);
      const right = Math.min(xEnd, chartArea.right);
      const name = displayName(bucket.label);
      const color = colors[bucket.label] || '#666';

      const last = mergedRegions[mergedRegions.length - 1];
      if (last && last.name === name) {
        last.right = right;
      } else {
        mergedRegions.push({ name, left, right, color });
      }
    });

    ctx.save();
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    mergedRegions.forEach(region => {
      const centerX = (region.left + region.right) / 2;
      const y = chartArea.bottom - 6;
      ctx.fillStyle = region.color;
      ctx.fillText(region.name, centerX, y);
    });
    ctx.restore();
  },
};

// 🎨 פונקציה גנרית ליצירת grid configuration שמדגיש את קו Y=0
// שימושי כאשר יש ערכים שליליים בתרשים
export const getHighlightedZeroLineGridConfig = () => ({
  color: (context: any) => {
    // הדגש את קו ה-Y=0 אם יש ערכים שליליים
    return context.tick.value === 0 ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)';
  },
  lineWidth: (context: any) => {
    // הפוך את קו ה-Y=0 לעבה יותר
    return context.tick.value === 0 ? 1.5 : 1;
  },
});
